import React, {Fragment, useState} from 'react';

import {browserHistory} from 'react-router';

import 'app/subscriptions';
import {GenericAttachedItemsSidebarItem} from 'app/components/general/GenericAttachedItemsSidebar/GenericAttachedItemsSidebarItem';
import {StyledListDIV} from 'app/components/general/VersionsSidebar/VersionsSidebar';
import {
  useInfiniteListFetcherRTK,
  UseQueryType,
} from 'app/general/datafetchers/hooks/useInfiniteListFetcherRTK';
import {
  DocSidebarPagePanels,
  StyledFooterDIV,
} from 'app/general/structures/DocSidebarPagePanels';
import {SweetLoadingBar} from 'app/general/sweet-loading-bar';
import {SearchInputField} from 'app/general/widgets/SearchInputField';
import StateButton from 'app/general/widgets/StateButton';
import {SORT_BY_TYPES} from 'app/utils/constants';
import {gt} from 'app/utils/i18n';

import type {Props as GenericAttachedItemSidebarProps} from 'app/components/general/GenericAttachedItemsSidebar/GenericAttachedItemsSidebarItem';
import type {
  ModelChangeMessageType,
  ModelSubscribedComponentPropsType,
} from 'app/subscriptions';
import type {ComponentType, ReactElement, ReactNode} from 'react';

export type GenericSidebarItemType = {
  readonly id: string | number;
  readonly name: string;
  readonly url: string;
  readonly can_edit: boolean;
  readonly html_url: string;
};

type ItemProps<T> = {
  nameProcessor?: (item: T) => ReactNode;
  htmlURLProcessor?: (item: T) => string | undefined;
  disableAndShowTooltipText?: string;
  actionTooltip?: string;
  item?: T;
  disabled?: boolean;
  attached?: boolean;
  onRemove?: (item: T) => void;
  onSelect?: (item: T) => Promise<unknown>;
};

type OwnProps<FP> = {
  title: ReactNode;
  showFilterField: boolean;
  onClose: () => void;
  attachedItems: Array<GenericSidebarItemType>;
  handleModelChange?: (
    message: ModelChangeMessageType,
    item?: GenericSidebarItemType
  ) => unknown;
  itemNameProcessor: (
    item: GenericSidebarItemType
  ) => string | ReactElement<'span'>;
  ItemComponent?: ComponentType<
    GenericAttachedItemSidebarProps<GenericSidebarItemType>
  >;
  itemComponentProps?:
    | ItemProps<GenericSidebarItemType>
    | ((
        item: GenericSidebarItemType,
        attached: boolean
      ) => ItemProps<GenericSidebarItemType>);
  onRemove: (item: unknown) => Promise<unknown>;
  canCreate: boolean;
  createURL: string;
  createLabel: string;
  addTooltipText?: string;
  disabledTooltipText?: string;
  searchPlaceholder: string;
  onAttach: (item: unknown) => Promise<unknown>;
  emptyResultsComponents: ReactNode;
  useDataHook: UseQueryType<FP, GenericSidebarItemType>;
  paramsConverter: (query: Record<string, unknown>) => FP;
};
type Props<FP extends {page: string}> = ModelSubscribedComponentPropsType &
  OwnProps<FP>;

const filterObjects = (response: Array<GenericSidebarItemType>) =>
  response.filter((p) => p.can_edit);

export function RTKGenericAttachedItemsSidebar<FP extends {page: string}>({
  title,
  showFilterField,
  onClose,
  attachedItems,
  itemNameProcessor,
  itemComponentProps,
  onRemove,
  canCreate,
  createURL,
  createLabel,
  searchPlaceholder,
  onAttach,
  emptyResultsComponents,
  onModelChange,
  handleModelChange,
  useDataHook,
  paramsConverter,
  ItemComponent = GenericAttachedItemsSidebarItem,
}: Props<FP>) {
  const [searchQuery, setSearchQuery] = useState<string | undefined>();

  let {
    results: items,
    isFetching: loading,
    loadNext,
    hasNext,
  } = useInfiniteListFetcherRTK({
    fetchParams: {
      ordering: SORT_BY_TYPES.ALPHABETHICALLY,
      search: searchQuery,
      page: 1,
      search_fields: 'name',
    },
    useDataHook,
    paramsConverter,
  });

  items = filterObjects(items);

  if (onModelChange != null && handleModelChange) {
    onModelChange(handleModelChange);
  }

  const getItemProps = (item: GenericSidebarItemType, attached: boolean) => {
    if (itemComponentProps == null) {
      return {};
    }

    return typeof itemComponentProps === 'function'
      ? itemComponentProps(item, attached)
      : itemComponentProps;
  };

  const handleAttach = (newItem: unknown) => {
    if (onAttach != null) {
      return onAttach(newItem);
    }

    throw 'Missing onAttach prop';
  };
  const handleRemove = (item: GenericSidebarItemType) => {
    if (onRemove != null) {
      return onRemove(item);
    }

    throw 'Missing onRemove prop';
  };

  const getAvailableItems = () => {
    const selectedItems = attachedItems.map((item) => item.url);

    return items.filter((item) => selectedItems.indexOf(item.url) === -1);
  };

  const search = (query: string) => {
    setSearchQuery(query);
  };

  const availableItems = getAvailableItems();
  return (
    <DocSidebarPagePanels
      title={title}
      additionalHeader={
        <Fragment>
          <div className="position-relative">
            <SweetLoadingBar loading={loading ? 1 : 0} />
          </div>
          {showFilterField ? (
            <SearchInputField
              className="form-control-sm"
              initialValue=""
              placeholder={searchPlaceholder}
              onChange={search}
              isSearching={loading}
            />
          ) : null}
        </Fragment>
      }
      onClose={onClose}
      footer={
        canCreate === true ? (
          <StyledFooterDIV>
            <button
              className="btn btn-secondary btn-sm btn-block"
              onClick={() =>
                browserHistory.push(`${createURL}?name=${searchQuery}`)
              }
            >
              <i className="fa fa-plus-circle" /> {createLabel}
            </button>
          </StyledFooterDIV>
        ) : null
      }
    >
      {availableItems.length === 0 && attachedItems.length === 0 && !loading ? (
        emptyResultsComponents
      ) : (
        <StyledListDIV>
          <ul className="list-group">
            {attachedItems.map((item) => (
              <ItemComponent
                key={item.url}
                item={item}
                attached={true}
                onSelect={handleAttach}
                nameProcessor={itemNameProcessor}
                onRemove={handleRemove}
                {...getItemProps(item, true)}
              />
            ))}
            {availableItems.map((item) => (
              <ItemComponent
                onRemove={handleRemove}
                key={item.url}
                item={item}
                attached={false}
                nameProcessor={itemNameProcessor}
                onSelect={handleAttach}
                {...getItemProps(item, false)}
              />
            ))}
            {hasNext ? (
              <div className="text-center my-2 mx-4">
                <StateButton
                  className="btn btn-outline-primary btn-sm btn-block"
                  busyText={gt('Loading...')}
                  onClick={async () => loadNext()}
                >
                  {gt('Load more')}
                </StateButton>
              </div>
            ) : null}
          </ul>
        </StyledListDIV>
      )}
    </DocSidebarPagePanels>
  );
}
